import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { analyticsService } from '../../../services/analyticsService';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const AnalyticsCharts = ({ analyticsData, projectId, dateRange }) => {
  const [conversionFunnel, setConversionFunnel] = useState([]);
  const [advisorPerformance, setAdvisorPerformance] = useState([]);
  const [campaignROI, setCampaignROI] = useState([]);
  const [timeSeriesData, setTimeSeriesData] = useState([]);

  useEffect(() => {
    loadChartData();
  }, [projectId, dateRange]);

  const loadChartData = async () => {
    try {
      const [funnel, performance, roi, timeSeries] = await Promise.all([
        analyticsService?.getConversionFunnel(projectId),
        analyticsService?.getAdvisorPerformance(dateRange?.startDate, dateRange?.endDate),
        analyticsService?.getCampaignROI(),
        analyticsService?.getTimeSeriesData('leads', projectId, dateRange?.startDate, dateRange?.endDate)
      ]);
      setConversionFunnel(funnel);
      setAdvisorPerformance(performance);
      setCampaignROI(roi);
      setTimeSeriesData(timeSeries);
    } catch (error) {
      console.error('Error loading chart data:', error);
    }
  };

  const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#6BCF7F'];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 0
    })?.format(value);
  };

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl p-6 border border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Total Leads</span>
            <Icon name="Users" size={20} className="text-primary" />
          </div>
          <div className="text-3xl font-bold text-foreground">
            {analyticsData?.totalLeads || 0}
          </div>
        </div>
        <div className="bg-card rounded-xl p-6 border border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Valor Total</span>
            <Icon name="DollarSign" size={20} className="text-success" />
          </div>
          <div className="text-3xl font-bold text-foreground">
            {formatCurrency(analyticsData?.totalValue || 0)}
          </div>
        </div>
        <div className="bg-card rounded-xl p-6 border border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Tasa de Conversión</span>
            <Icon name="TrendingUp" size={20} className="text-accent" />
          </div>
          <div className="text-3xl font-bold text-foreground">
            {analyticsData?.conversionRate?.toFixed(1) || 0}%
          </div>
        </div>
        <div className="bg-card rounded-xl p-6 border border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Leads Calientes</span>
            <Icon name="Flame" size={20} className="text-error" />
          </div>
          <div className="text-3xl font-bold text-foreground">
            {analyticsData?.byInterest?.hot || 0}
          </div>
        </div>
      </div>
      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion Funnel */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Icon name="Filter" size={20} className="mr-2 text-primary" />
            Embudo de Conversión
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={conversionFunnel}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="stage" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
                formatter={(value) => value}
              />
              <Legend />
              <Bar dataKey="count" fill="#4ECDC4" name="Leads" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Leads by Source */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Icon name="Share2" size={20} className="mr-2 text-primary" />
            Leads por Fuente
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={Object.entries(analyticsData?.bySource || {})?.map(([key, value]) => ({
                  name: key,
                  value
                }))}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {Object.entries(analyticsData?.bySource || {})?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Time Series */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Icon name="Activity" size={20} className="mr-2 text-primary" />
            Tendencia de Leads
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeSeriesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
              />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#4ECDC4" name="Leads" strokeWidth={2} />
              <Line type="monotone" dataKey="conversions" stroke="#6BCF7F" name="Conversiones" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Advisor Performance */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Icon name="Award" size={20} className="mr-2 text-primary" />
            Rendimiento de Asesores
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={advisorPerformance?.slice(0, 5)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="advisorName" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
              />
              <Legend />
              <Bar dataKey="totalLeads" fill="#45B7D1" name="Total Leads" />
              <Bar dataKey="closedWon" fill="#6BCF7F" name="Cerrados" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Campaign ROI Table */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Target" size={20} className="mr-2 text-primary" />
          ROI de Campañas
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Campaña</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Plataforma</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Presupuesto</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Conversiones</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">ROI</th>
              </tr>
            </thead>
            <tbody>
              {campaignROI?.map(campaign => (
                <tr key={campaign?.id} className="border-b border-border hover:bg-muted/30">
                  <td className="py-3 px-4 text-sm text-foreground">{campaign?.name}</td>
                  <td className="py-3 px-4 text-sm text-foreground capitalize">{campaign?.platform}</td>
                  <td className="py-3 px-4 text-sm text-foreground text-right">
                    {formatCurrency(campaign?.budget)}
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground text-right">{campaign?.conversions}</td>
                  <td className="py-3 px-4 text-sm text-right">
                    <span className={`font-medium ${
                      campaign?.roi > 0 ? 'text-success' : 'text-error'
                    }`}>
                      {campaign?.roi?.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsCharts;